import React from "react";
import { Alert, Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { NavBar } from "../../common/NavBar";
import BackdropLoadingScreen from "../../common/BackdropLoadingScreen";
import { useAppStore } from "../../../appStore";
import { createApi } from "../../utils/createApi";
import { getParentAccounts } from "../../utils/getParentAccounts";
import { fetchAllWireGuardTunnels } from "./fetchWireGuardTunnels";
import { fetchAllEdgeDevices } from "./fetchEdgeDevices";
import { WireGuardTunnelsTable } from "./WireGuardTunnelsTable";
import { EdgeDevicesTable } from "./EdgeDevicesTable";
import { WireGuardTunnelDetailsDrawer } from "./WireGuardTunnelDetailsDrawer";
import { WireGuardTunnelsFiltersPanel } from "./WireGuardTunnelsFiltersPanel";
import { WireGuardAddTunnelDialog } from "./WireGuardAddTunnelDialog";
import { AddEdgeDeviceDialog } from "./AddEdgeDeviceDialog";
import { WireGuardBulkUploadDialog } from "./WireGuardBulkUploadDialog";
import { WireGuardPageTabs } from "./WireGuardPageTabs";
import { EdgeDeviceDetailsDrawer } from "./EdgeDeviceDetailsDrawer";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import {
  Dispatcher,
  ParentAccount,
  WireGuardFilterState,
  WireGuardServerOption,
  WireGuardTunnel,
} from "./wireGuardTunnelsInterfaces";
import { applyWireGuardFilters } from "./wireGuardTunnelsFilterUtils";
import { AccessibleGroup, getAccessibleGroups } from "./wireGuardGroupUtils";
import { getComputedTunnelStatus } from "./wireGuardTunnelStatusUtils";
import { EdgeDevice, WireGuardPageTab } from "./edgeDeviceInterfaces";
import { applyEdgeDeviceSearch } from "./edgeDeviceFilterUtils";

const defaultFilters: WireGuardFilterState = {
  groupIds: [],
  serverIds: [],
  tunnelStatuses: [],
  subnetVirtuallyAssigned: "all",
  searchText: "",
};

const VIEWPORT_LAYOUT_FLOOR_PX = 1545;
const EDGE_DEVICES_VIEWPORT_FLOOR_PX = 1300;
const FILTERS_PANEL_WIDTH_PX = 340;
const LAYOUT_STACK_GAP_PX = 16;
const CONTENT_RIGHT_INSET_PX = 18;
const TAB_BAND_HEIGHT_PX = 49;

export const WireGuardTunnels = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const navigate = useNavigate();
  const { wireGuardOptions, userPermissions } = useAppStore();
  const { isFeatureEnabled } = useFeatureFlag();
  // ENG-278: creating/editing tunnels needs the create_wireguard_tunnels
  // permission. Viewing the list does not, so only the write controls are gated.
  const canCreateTunnels = Boolean(userPermissions?.create_wireguard_tunnels);
  // Every group the user can assign a tunnel to (ENG-272). Cached at login, but
  // fall back to the parent accounts this page fetches live so a cold or evicted
  // cache can't leave the required Group field with nothing to pick.
  const cachedGroups = React.useMemo(() => getAccessibleGroups(), []);
  const showEdgeDevices = isFeatureEnabled("showEdgeDevices", false);

  const [activeTab, setActiveTab] =
    React.useState<WireGuardPageTab>("wireguard-tunnels");
  const [isLoading, setIsLoading] = React.useState(true);
  const [edgeDevicesLoading, setEdgeDevicesLoading] = React.useState(false);
  const [parentAccountsLoading, setParentAccountsLoading] =
    React.useState(true);
  const [fetchError, setFetchError] = React.useState("");
  const [allTunnels, setAllTunnels] = React.useState<WireGuardTunnel[]>([]);
  const [allEdgeDevices, setAllEdgeDevices] = React.useState<EdgeDevice[]>([]);
  const [edgeDeviceSearchText, setEdgeDeviceSearchText] = React.useState("");
  const [selectedTunnel, setSelectedTunnel] =
    React.useState<WireGuardTunnel | null>(null);
  const [selectedEdgeDevice, setSelectedEdgeDevice] =
    React.useState<EdgeDevice | null>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [edgeDeviceDetailsOpen, setEdgeDeviceDetailsOpen] =
    React.useState(false);
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [addEdgeDeviceDialogOpen, setAddEdgeDeviceDialogOpen] =
    React.useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = React.useState(false);
  const [filters, setFilters] =
    React.useState<WireGuardFilterState>(defaultFilters);
  const [parentAccounts, setParentAccounts] = React.useState<ParentAccount[]>(
    []
  );
  const accessibleGroups = React.useMemo<AccessibleGroup[]>(
    () =>
      cachedGroups.length > 0
        ? cachedGroups
        : parentAccounts.map((account) => ({
            id: account.id,
            name: account.name,
            parent: null,
            parent_account: true,
          })),
    [cachedGroups, parentAccounts]
  );
  const [, setParentIds] = React.useState<Map<string, number>>(new Map());
  const [parentNames, setParentNames] = React.useState<Map<number, string>>(
    new Map()
  );
  const [page, setPage] = React.useState(0);
  const [edgeDevicesPage, setEdgeDevicesPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [edgeDevicesRowsPerPage, setEdgeDevicesRowsPerPage] =
    React.useState(10);
  const [isExportingCsv, setIsExportingCsv] = React.useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] =
    React.useState(false);

  React.useEffect(() => {
    getParentAccounts(
      setParentAccountsLoading,
      setParentAccounts,
      setParentIds,
      setParentNames,
      navigate
    );
  }, [navigate]);

  const loadTunnels = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const tunnels = await fetchAllWireGuardTunnels();
      setAllTunnels(tunnels);
      setFetchError("");
      return tunnels;
    } catch {
      setFetchError("Failed to load WireGuard tunnels.");
      setAllTunnels([]);
      return [] as WireGuardTunnel[];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadEdgeDevices = React.useCallback(async () => {
    setEdgeDevicesLoading(true);
    try {
      const devices = await fetchAllEdgeDevices();
      setAllEdgeDevices(devices);
      setFetchError("");
      return devices;
    } catch {
      setFetchError("Failed to load edge devices.");
      setAllEdgeDevices([]);
      return [] as EdgeDevice[];
    } finally {
      setEdgeDevicesLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadTunnels();
  }, [loadTunnels]);

  React.useEffect(() => {
    if (showEdgeDevices && activeTab === "edge-devices") {
      loadEdgeDevices();
    }
  }, [activeTab, loadEdgeDevices, showEdgeDevices]);

  React.useEffect(() => {
    if (activeTab === "wireguard-tunnels") {
      setEdgeDeviceDetailsOpen(false);
      setSelectedEdgeDevice(null);
    } else {
      setDetailsOpen(false);
      setSelectedTunnel(null);
    }
  }, [activeTab]);

  const getGroupName = React.useCallback(
    (groupId: number | null) => {
      if (!groupId) {
        return "Unassigned";
      }
      if (parentNames.has(groupId)) {
        return parentNames.get(groupId) || "Unknown group";
      }
      const fromAccounts = parentAccounts.find(
        (account) => account.id === groupId
      );
      if (fromAccounts?.name) {
        return fromAccounts.name;
      }
      // parentAccounts only holds parent accounts, so a tunnel on a site-level
      // subgroup falls through to the full accessible list.
      const fromAccessible = accessibleGroups.find(
        (group) => group.id === groupId
      );
      return fromAccessible?.name || `Unknown group ${groupId}`;
    },
    [accessibleGroups, parentAccounts, parentNames]
  );

  const getServerName = React.useCallback(
    (serverId: number | null) => {
      if (!serverId) {
        return "Unassigned";
      }
      const matchedOption = wireGuardOptions.find(
        (option: { id: number; name: string }) => option.id === serverId
      );
      return matchedOption?.name || `Unknown server ${serverId}`;
    },
    [wireGuardOptions]
  );

  const getServerPublicKey = React.useCallback(
    (serverId: number | null) => {
      if (!serverId) {
        return "-";
      }
      const matchedOption = wireGuardOptions.find(
        (option: { id: number }) => option.id === serverId
      );
      return matchedOption?.server_public_key || "-";
    },
    [wireGuardOptions]
  );

  const filteredRows = React.useMemo(
    () => applyWireGuardFilters(allTunnels, filters, parentAccounts),
    [allTunnels, filters, parentAccounts]
  );

  const filteredEdgeDevices = React.useMemo(
    () => applyEdgeDeviceSearch(allEdgeDevices, edgeDeviceSearchText),
    [allEdgeDevices, edgeDeviceSearchText]
  );

  const tunnelStatuses = React.useMemo(() => {
    return Array.from(
      new Set(
        allTunnels
          .map((tunnel) => getComputedTunnelStatus(tunnel.last_handshake))
          .filter((status) => Boolean(status))
      )
    );
  }, [allTunnels]);

  const groupFilterOptions = React.useMemo(() => {
    const groupIdsInRows = new Set<number>();
    allTunnels.forEach((tunnel) => {
      if (typeof tunnel.group === "number") {
        groupIdsInRows.add(tunnel.group);
      }
    });

    const options = Array.from(groupIdsInRows).map((groupId) => ({
      id: groupId,
      name: getGroupName(groupId),
    }));

    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [allTunnels, getGroupName]);

  React.useEffect(() => {
    setPage(0);
  }, [filters]);

  React.useEffect(() => {
    setEdgeDevicesPage(0);
  }, [edgeDeviceSearchText]);

  const handleExportCsv = async () => {
    setIsExportingCsv(true);
    const AxiosUI = createApi("");
    try {
      const searchParams = new URLSearchParams();
      searchParams.set("all_levels", "true");

      if (filters.searchText.trim()) {
        searchParams.set("search", filters.searchText.trim());
      }

      filters.groupIds.forEach((groupId) => {
        searchParams.append("group", String(groupId));
      });
      filters.serverIds.forEach((serverId) => {
        searchParams.append("server", String(serverId));
      });
      filters.tunnelStatuses.forEach((status) => {
        searchParams.append("tunnel_status", status.toLowerCase());
      });

      if (filters.subnetVirtuallyAssigned === "yes") {
        searchParams.set("subnets_are_virtually_assigned", "true");
      } else if (filters.subnetVirtuallyAssigned === "no") {
        searchParams.set("subnets_are_virtually_assigned", "false");
      }

      const endpoint = `wireguard_tunnel/export-csv/?${searchParams.toString()}`;
      const response = await AxiosUI.get(endpoint, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      saveAs(blob, "wireguard_tunnels_export.csv");
      setFetchError("");
    } catch {
      setFetchError("Failed to export WireGuard tunnels CSV.");
    } finally {
      setIsExportingCsv(false);
    }
  };

  const handleDownloadCsvTemplate = async () => {
    setIsDownloadingTemplate(true);
    const AxiosUI = createApi("");
    try {
      const response = await AxiosUI.get("wireguard_tunnel/csv-template/", {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      saveAs(blob, "wireguard_tunnels_template.csv");
      setFetchError("");
    } catch {
      setFetchError("Failed to download WireGuard tunnels CSV template.");
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleOpenWireguardTunnelFromEdgeDevice = React.useCallback(
    (tunnel: WireGuardTunnel) => {
      setEdgeDeviceDetailsOpen(false);
      setSelectedEdgeDevice(null);
      setSelectedTunnel(tunnel);
      setDetailsOpen(true);
      setActiveTab("wireguard-tunnels");
    },
    []
  );

  const leftOffset = navBarCollapsed ? 75 : 150;
  const tabsLeftOffset = navBarCollapsed ? 95 : 170;
  const contentTopOffset = TAB_BAND_HEIGHT_PX + 9;
  const contentMinWidth =
    VIEWPORT_LAYOUT_FLOOR_PX - leftOffset - CONTENT_RIGHT_INSET_PX;
  const tableMinWidth =
    contentMinWidth - FILTERS_PANEL_WIDTH_PX - LAYOUT_STACK_GAP_PX;
  const edgeDevicesContentMinWidth =
    EDGE_DEVICES_VIEWPORT_FLOOR_PX - leftOffset - CONTENT_RIGHT_INSET_PX;
  // The VPN Debugger used to be a third tab here; it lives on the
  // Troubleshooter's WireGuard tab now. A stale link or a hidden Edge Devices
  // flag both fall back to the tunnel list rather than rendering nothing.
  const effectiveTab: WireGuardPageTab =
    activeTab === "edge-devices" && !showEdgeDevices
      ? "wireguard-tunnels"
      : activeTab;

  return (
    <div>
      <BackdropLoadingScreen
        openState={isLoading || parentAccountsLoading || edgeDevicesLoading}
      />
      <div
        className="absolute w-screen h-screen"
        style={{
          background: `linear-gradient(to bottom, #FFFFFF 0px, #FFFFFF ${TAB_BAND_HEIGHT_PX}px, #E5E5E5 ${TAB_BAND_HEIGHT_PX}px, #E5E5E5 100%)`,
        }}
      >
        <div
          className="absolute top-0 right-[1px] z-10"
          style={{ left: `${tabsLeftOffset}px` }}
        >
          <WireGuardPageTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            showEdgeDevices={showEdgeDevices}
          />
        </div>
        <Box
          sx={{
            position: "absolute",
            top: `${contentTopOffset}px`,
            left: `${leftOffset}px`,
            right: "10px",
            bottom: "10px",
            pr: 1,
          }}
        >
          {fetchError && (
            <Alert sx={{ fontFamily: "Mulish", mb: 2 }} severity="error">
              {fetchError}
            </Alert>
          )}
          {effectiveTab === "wireguard-tunnels" ? (
            <Stack
              direction="row"
              gap={2}
              alignItems="stretch"
              sx={{
                height: "100%",
                minHeight: 0,
                minWidth: contentMinWidth,
                overflowX: "auto",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: tableMinWidth,
                  height: "100%",
                  minHeight: 0,
                  flexShrink: 0,
                }}
              >
                <WireGuardTunnelsTable
                  rows={filteredRows}
                  searchText={filters.searchText}
                  onSearchTextChange={(value) =>
                    setFilters((prev) => ({ ...prev, searchText: value }))
                  }
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  onRowClick={(row) => {
                    setSelectedTunnel(row);
                    setDetailsOpen(true);
                  }}
                  getGroupName={getGroupName}
                  getServerName={getServerName}
                  onDownloadCsvTemplate={handleDownloadCsvTemplate}
                  isDownloadingTemplate={isDownloadingTemplate}
                  onBulkUploadClick={() => setBulkUploadOpen(true)}
                  onExportCsv={handleExportCsv}
                  isExportingCsv={isExportingCsv}
                  onAddTunnelClick={() => setAddDialogOpen(true)}
                  canCreateTunnels={canCreateTunnels}
                  onDeleteSelectedRows={async (selectedIds) => {
                    const AxiosUI = createApi("");
                    try {
                      await Promise.all(
                        selectedIds.map((id) =>
                          AxiosUI.delete(`wireguard_tunnel/${id}/`)
                        )
                      );
                      const freshTunnels = await loadTunnels();
                      if (
                        selectedTunnel &&
                        selectedIds.includes(selectedTunnel.id)
                      ) {
                        setDetailsOpen(false);
                        setSelectedTunnel(null);
                      } else if (selectedTunnel) {
                        const refreshedTunnel =
                          freshTunnels.find(
                            (tunnel) => tunnel.id === selectedTunnel.id
                          ) || null;
                        setSelectedTunnel(refreshedTunnel);
                      }
                    } catch {
                      setFetchError(
                        "Failed to delete selected WireGuard tunnels."
                      );
                    }
                  }}
                />
              </Box>
              <Box sx={{ height: "100%", minHeight: 0, flexShrink: 0 }}>
                <WireGuardTunnelsFiltersPanel
                  filters={filters}
                  setFilters={setFilters}
                  parentAccounts={groupFilterOptions}
                  wireGuardOptions={wireGuardOptions}
                  tunnelStatuses={tunnelStatuses}
                />
              </Box>
            </Stack>
          ) : (
            <Box
              sx={{
                height: "100%",
                minHeight: 0,
                minWidth: edgeDevicesContentMinWidth,
                overflowX: "auto",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  minHeight: 0,
                  minWidth: edgeDevicesContentMinWidth,
                  flexShrink: 0,
                }}
              >
                <EdgeDevicesTable
                  rows={filteredEdgeDevices}
                  searchText={edgeDeviceSearchText}
                  onSearchTextChange={setEdgeDeviceSearchText}
                  page={edgeDevicesPage}
                  rowsPerPage={edgeDevicesRowsPerPage}
                  onPageChange={(_, newPage) => setEdgeDevicesPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setEdgeDevicesRowsPerPage(parseInt(event.target.value, 10));
                    setEdgeDevicesPage(0);
                  }}
                  onAddEdgeDeviceClick={() => setAddEdgeDeviceDialogOpen(true)}
                  onRowClick={(row) => {
                    setSelectedEdgeDevice(row);
                    setEdgeDeviceDetailsOpen(true);
                  }}
                  wireguardTunnels={allTunnels}
                  onOpenWireguardTunnel={
                    handleOpenWireguardTunnelFromEdgeDevice
                  }
                />
              </Box>
            </Box>
          )}
        </Box>
      </div>
      {showEdgeDevices && (
        <AddEdgeDeviceDialog
          open={addEdgeDeviceDialogOpen}
          onClose={() => {
            setAddEdgeDeviceDialogOpen(false);
            if (activeTab === "edge-devices") {
              loadEdgeDevices();
            }
          }}
        />
      )}
      <WireGuardAddTunnelDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onTunnelCreated={async () => {
          await loadTunnels();
        }}
        wireGuardOptions={wireGuardOptions as WireGuardServerOption[]}
        accessibleGroups={accessibleGroups}
      />
      <WireGuardBulkUploadDialog
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        accessibleGroups={accessibleGroups}
        onUploadSuccess={async (validateOnlyMode) => {
          if (!validateOnlyMode) {
            await loadTunnels();
          }
        }}
      />
      <WireGuardTunnelDetailsDrawer
        open={detailsOpen}
        selectedTunnel={selectedTunnel}
        onClose={() => setDetailsOpen(false)}
        canCreateTunnels={canCreateTunnels}
        getGroupName={getGroupName}
        getServerName={getServerName}
        getServerPublicKey={getServerPublicKey}
        onTunnelPatched={async (tunnelId) => {
          const freshTunnels = await loadTunnels();
          const refreshedTunnel =
            freshTunnels.find((tunnel) => tunnel.id === tunnelId) || null;
          setSelectedTunnel(refreshedTunnel);
        }}
        onTunnelDeleted={async () => {
          await loadTunnels();
          setSelectedTunnel(null);
        }}
      />
      {showEdgeDevices && (
        <EdgeDeviceDetailsDrawer
          open={edgeDeviceDetailsOpen}
          selectedDevice={selectedEdgeDevice}
          onClose={() => setEdgeDeviceDetailsOpen(false)}
          wireguardTunnels={allTunnels}
          onOpenWireguardTunnel={handleOpenWireguardTunnelFromEdgeDevice}
        />
      )}
      <div className="justify-start">
        <NavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
          props={{}}
        />
      </div>
    </div>
  );
};
